'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import getLang from 'I18n/utils/getLang';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { NavMarker } from 'react-rhumb';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import TrackAwardModal from '../../components/Modals/SuccessModals/TrackAwardModal';
import UsageTracker from '../../utils/UsageTracker';
import { fetchTrackByIdIfNeeded } from '../../actions/TrackActions';
import { sendToUpwork } from '../../actions/AppActions';
import { fetchAllAchievements } from '../../actions/AchievementActions';
import { getTrack, getTracksAreRequesting } from '../../selectors/trackSelectors';
import { useTrackAwardProgress } from '../../features/userProgress';
import { useExamAttempts } from '../../features/exams';

class TrackAwardModalContainer extends PureComponent {
  constructor(props) {
    super(props);

    this.trackViewModal = () => {
      this.trackUsage('view-modal');
    };

    this.handleViewConnectDotCom = () => {
      this.trackUsage('view-connectdotcom');
    };

    this.handleJoinConnectDotCom = () => {
      this.trackUsage('join-connectdotcom');
    };

    this.trackUsage = this.trackUsage.bind(this);
  }

  componentDidMount() {
    const {
      fetchAchievements,
      fetchTrack,
      match: {
        params: {
          trackId
        }
      }
    } = this.props;
    this.maybeRedirectToBasePage();
    fetchTrack(trackId, getLang());
    fetchAchievements(getLang());
  }

  componentDidUpdate() {
    this.maybeRedirectToBasePage();
  }

  maybeRedirectToBasePage() {
    const {
      history: {
        replace
      },
      match: {
        params
      },
      tracksNotYetLoaded,
      track
    } = this.props;

    if (!tracksNotYetLoaded && this.isAwardInvalid(track)) {
      // only render the award modal if there is an actual award with the same name
      replace(params[0] || '/');
    }
  }

  isAwardInvalid(track) {
    const {
      award,
      match: {
        params: {
          awardName
        }
      }
    } = this.props;
    return !award || !track || award.awardName !== awardName;
  }

  trackUsage(action, socialName) {
    const {
      track,
      award
    } = this.props;
    const trackedProps = {
      action,
      awardName: award.awardName,
      trackId: track.get('id'),
      trackName: track.get('name'),
      view: 'track-award-modal'
    };

    if (typeof socialName === 'string') {
      trackedProps.socialName = socialName;
    }

    UsageTracker.track('awardInteraction', trackedProps);
  }

  render() {
    const {
      award,
      examAttempt,
      firstName,
      history: {
        push
      },
      location: {
        search
      },
      track,
      tracksNotYetLoaded,
      match: {
        params = []
      }
    } = this.props;

    if (!(track && track.get('id')) && tracksNotYetLoaded) {
      return /*#__PURE__*/_jsx(UILoadingOverlay, {});
    }

    if (this.isAwardInvalid(track)) {
      return null; // the redirect will do the rest
    }

    const returnUrl = `${params[0] || '/'}${search}`;
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "COURSE_AWARD_VIEW_LOADED",
      children: /*#__PURE__*/_jsx(TrackAwardModal, {
        award: award,
        examAttempt: examAttempt,
        firstName: firstName,
        onClickJoinConnectDotCom: this.handleJoinConnectDotCom,
        onClickShareThis: () => this.trackUsage('click-share-this-cert'),
        onClickViewConnectDotCom: this.handleViewConnectDotCom,
        onClose: (...partialArgs) => push(returnUrl, undefined, ...partialArgs),
        onDownloadCertificate: (...partialArgs) => this.trackUsage('download-certificate', ...partialArgs),
        onMount: this.trackViewModal,
        track: track
      })
    });
  }

}

TrackAwardModalContainer.propTypes = {
  award: PropTypes.object,
  examAttempt: PropTypes.object,
  fetchAchievements: PropTypes.func.isRequired,
  fetchTrack: PropTypes.func.isRequired,
  firstName: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      awardName: PropTypes.string.isRequired,
      trackId: PropTypes.string.isRequired
    }).isRequired
  }).isRequired,
  sendAwardToUpwork: PropTypes.func.isRequired,
  track: ImmutablePropTypes.map,
  tracksNotYetLoaded: PropTypes.bool
};

function makeMapStateToProps(state, ownProps) {
  const {
    app,
    auth: {
      user
    }
  } = state;
  const {
    match: {
      params: {
        trackId
      }
    }
  } = ownProps;
  return {
    sendToUpworkHasError: app.sendToUpworkHasError,
    sendToUpworkIsRequesting: app.sendToUpworkIsRequesting,
    track: getTrack(state, {
      trackId
    }),
    tracksNotYetLoaded: getTracksAreRequesting(state),
    firstName: user.firstName
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchTrack: fetchTrackByIdIfNeeded,
    sendAwardToUpwork: sendToUpwork,
    fetchAchievements: fetchAllAchievements
  }, dispatch);
}

const WithAwardAttemptTrackAwardModalContainer = (_ref) => {
  let {
    match
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["match"]);

  const trackId = match.params.trackId;
  const {
    award
  } = useTrackAwardProgress(Number(trackId));
  const {
    examAttempts
  } = useExamAttempts(Number(trackId));
  const examAttempt = award && examAttempts && award.examAttemptId ? examAttempts[award.examAttemptId] : null;
  return /*#__PURE__*/_jsx(TrackAwardModalContainer, Object.assign({
    award: award,
    examAttempt: examAttempt,
    match: match,
    trackId: trackId
  }, props));
};

export default connect(makeMapStateToProps, mapDispatchToProps)(WithAwardAttemptTrackAwardModalContainer);